import { DownloadIcon } from 'lucide-react';
import type { JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import type { WorkspaceArtifact } from '@/lib/api/workspace-artifacts.js';

import { safeDownload } from './artifact-download.js';
import { formatBytes } from './artifact-display.js';
import { ArtifactContentBody } from './ArtifactPreview.js';
import { FileViewerShell } from './FileViewerShell.js';
import { FullScreenToggle } from './FullScreenToggle.js';
import { useFullScreenShortcut } from './useFullScreenShortcut.js';

/**
 * A file from the chat side panel, filling the window. The panel is too narrow to enlarge in place, and the
 * Artifacts page's own viewer is wired to that page, so the panel opens this smaller viewer in the same
 * {@link FileViewerShell}, always in full screen: the name and size, the preview of the version on screen,
 * and a download docked beside the close button. Leaving full screen closes it.
 */
export function ArtifactFullScreenDialog({
  workspaceId,
  artifact,
  revision,
  onClose,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  revision?: number;
  onClose: () => void;
}): JSX.Element {
  useFullScreenShortcut(true, onClose);
  return (
    <FileViewerShell
      title={artifact.name}
      titleMeta={artifact.size >= 0 ? formatBytes(artifact.size) : undefined}
      maximized
      onLeaveFullScreen={onClose}
      onClose={onClose}
      toolbar={
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={() => safeDownload(artifact, revision)}
          >
            <DownloadIcon className="size-4" /> Download
          </Button>
          <FullScreenToggle
            maximized
            onToggle={onClose}
            className="inline-flex"
          />
        </>
      }
    >
      <ArtifactContentBody
        workspaceId={workspaceId}
        artifact={artifact}
        revision={revision}
        fill
      />
    </FileViewerShell>
  );
}
